import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { ColorMode } from './index';

// Color scheme is shared app state — keep it in a Context so every consumer
// (ThemeProvider, toggle button, etc.) reads + writes the SAME instance.
// (The previous implementation was a local-state hook, which gave each call
// site its own state and broke the toggle.)

const STORAGE_KEY = 'sahib-color-scheme';

interface ColorSchemeValue {
  mode: ColorMode;
  setMode: (next: ColorMode) => void;
  toggle: () => void;
}

const ColorSchemeContext = createContext<ColorSchemeValue | null>(null);

function readInitialMode(): ColorMode {
  if (typeof window === 'undefined') return 'light';
  const saved = window.sessionStorage.getItem(STORAGE_KEY);
  if (saved === 'dark' || saved === 'light') return saved;
  return 'light';
}

interface ColorSchemeProviderProps {
  children: ReactNode;
}

export function ColorSchemeProvider({ children }: ColorSchemeProviderProps) {
  const [mode, setModeState] = useState<ColorMode>(readInitialMode);

  const setMode = useCallback((next: ColorMode) => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(STORAGE_KEY, next);
    }
    setModeState(next);
  }, []);

  const toggle = useCallback(() => {
    setModeState((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(STORAGE_KEY, next);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const value = useMemo<ColorSchemeValue>(
    () => ({ mode, setMode, toggle }),
    [mode, setMode, toggle],
  );

  return <ColorSchemeContext.Provider value={value}>{children}</ColorSchemeContext.Provider>;
}

export function useColorScheme(): ColorSchemeValue {
  const ctx = useContext(ColorSchemeContext);
  if (!ctx) {
    throw new Error(
      'useColorScheme must be used within <ThemeProvider> (which mounts ColorSchemeProvider).',
    );
  }
  return ctx;
}
